/**
 * 플레이어(검사). 상태 머신 + 물리 + 애니메이션.
 * 상태 전이는 setMode() 한 곳에서만 처리해 중력/애니메이션 복구 누락을 막는다.
 * 전이도는 docs/architecture.md 4.3 참고.
 */

import Phaser from 'phaser';
import { ANIM } from '../assets/animations';
import type { AssetTable } from '../assets/loader';
import { PLAYER, WORLD, type SkillDef } from '../data/balance';
import { ITEMS } from '../data/items';
import type { EquipSlot } from '../data/equipment';
import { DEPTH } from '../game/depth';
import type { InputSnapshot } from '../input/Controls';
import type { ClimbableInstance } from '../systems/TerrainBuilder';
import type { PlayerState } from '../systems/PlayerState';
import { training } from '../systems/Training';

export type PlayerMode = 'idle' | 'walk' | 'air' | 'climb' | 'attack' | 'hurt' | 'dead';

export const PLAYER_EVENT = {
  jump: 'jump',
  land: 'land',
  attackStart: 'attack-start',
  attackHit: 'attack-hit',
  climbEnter: 'climb-enter',
  climbStep: 'climb-step',
  hurt: 'hurt',
  died: 'died',
  noMp: 'no-mp',
} as const;

const BODY = { width: 34, height: 70 };

export class Player extends Phaser.Physics.Arcade.Sprite {
  readonly events = new Phaser.Events.EventEmitter();

  mode: PlayerMode = 'idle';
  facing: 1 | -1 = 1;
  /** 지금 ↑ 를 누르면 잡을 수 있는 사다리/밧줄 (UI 힌트용) */
  nearClimbable: ClimbableInstance | null = null;

  private readonly playerState: PlayerState;
  private readonly cooldowns = new Map<string, number>();
  private climbTarget: ClimbableInstance | null = null;
  private activeSkill: SkillDef | null = null;
  private attackElapsed = 0;
  private attackHitFired = false;
  private hurtTimer = 0;
  private invulnTimer = 0;
  private dropThroughTimer = 0;
  private climbStepAccum = 0;
  private wasAirborne = false;
  private blinkTween: Phaser.Tweens.Tween | null = null;
  private readonly look: Partial<Record<Exclude<EquipSlot, 'weapon'>, Phaser.GameObjects.Image>> = {};
  private weaponSpr!: Phaser.GameObjects.Image;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    assets: AssetTable,
    state: PlayerState,
  ) {
    super(scene, x, y, 'player-stand', 0);
    this.playerState = state;

    scene.add.existing(this);
    scene.physics.add.existing(this);

    const info = assets.get('player-stand');
    const scale = assets.scaleFor('player-stand');
    this.setOrigin(0.5, 1).setDepth(DEPTH.player).setScale(scale);

    const bw = BODY.width / scale;
    const bh = BODY.height / scale;
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setSize(bw, bh, false);
    body.setOffset((info.frameWidth - bw) / 2, info.frameHeight - bh);
    body.setCollideWorldBounds(true);
    body.setMaxVelocity(520, 1400);

    this.play(ANIM.stand, true);
    const lookSlots = ['cape', 'bottom', 'top', 'shoes', 'gloves', 'cap'] as const;
    for (const slot of lookSlots) {
      const img = scene.add.image(x, y, `look-${slot}`, 0).setOrigin(0.5, 1).setVisible(false);
      img.setScale(scale);
      this.look[slot] = img;
    }
    this.weaponSpr = scene.add.image(x, y, 'item-sword-1h', 0).setOrigin(0.5, 1).setVisible(false);
    this.weaponSpr.setScale(assets.scaleFor('item-sword-1h') * 0.9);
  }

  get isInvulnerable(): boolean {
    return this.invulnTimer > 0 || this.mode === 'dead';
  }

  get isDroppingThrough(): boolean {
    return this.dropThroughTimer > 0;
  }

  get isClimbing(): boolean {
    return this.mode === 'climb';
  }

  get isBusy(): boolean {
    return this.mode === 'attack' || this.mode === 'hurt' || this.mode === 'dead';
  }

  cooldownLeft(skillId: string): number {
    return Math.max(0, this.cooldowns.get(skillId) ?? 0);
  }

  override update(deltaMs: number, input: InputSnapshot, climbables: readonly ClimbableInstance[]): void {
    const dt = deltaMs / 1000;
    if (!this.playerState.alive && this.mode !== 'dead') this.die();

    for (const [id, left] of this.cooldowns) {
      if (left > 0) this.cooldowns.set(id, left - deltaMs);
    }
    if (this.invulnTimer > 0) this.invulnTimer -= deltaMs;
    if (this.dropThroughTimer > 0) this.dropThroughTimer -= deltaMs;

    const stunned = this.playerState.hasStatus('stun');
    if (stunned && this.mode === 'climb') this.exitClimb(this.onFloor() ? 'idle' : 'air');

    switch (this.mode) {
      case 'dead':
        this.syncLook();
        return;
      case 'hurt':
        this.hurtTimer -= deltaMs;
        if (this.hurtTimer <= 0) this.setMode(this.onFloor() ? 'idle' : 'air');
        this.syncLook();
        return;
      case 'attack':
        this.updateAttack(deltaMs);
        this.syncLook();
        return;
      case 'climb':
        this.updateClimb(deltaMs, input);
        this.syncLook();
        return;
      default:
        this.updateGround(dt, input, climbables);
    }
    this.syncLook();
  }

  private syncLook(): void {
    const flip = this.flipX;
    for (const [slot, img] of Object.entries(this.look)) {
      if (!img) continue;
      const piece = this.playerState.worn[slot as EquipSlot];
      const tint = piece ? (ITEMS[piece.itemId]?.equip?.lookTint ?? 0xffffff) : 0;
      img.setVisible(Boolean(piece));
      if (!piece) continue;
      img.setPosition(this.x, this.y);
      img.setFlipX(flip);
      img.setScale(this.scaleX, this.scaleY);
      img.setAlpha(this.alpha);
      img.setTint(tint);
      img.setDepth(slot === 'cape' ? DEPTH.player - 1 : DEPTH.player + 1);
    }
    const weapon = this.playerState.worn.weapon;
    const wdef = weapon ? ITEMS[weapon.itemId] : undefined;
    if (weapon && wdef) {
      this.weaponSpr.setTexture(wdef.texture, 0);
      this.weaponSpr.setVisible(true);
      this.weaponSpr.setPosition(this.x + (flip ? -18 : 18), this.y - 32);
      this.weaponSpr.setFlipX(flip);
      this.weaponSpr.setAlpha(this.alpha);
      this.weaponSpr.setDepth(DEPTH.player + 2);
      if (wdef.equip?.lookTint) this.weaponSpr.setTint(wdef.equip.lookTint);
      else this.weaponSpr.clearTint();
    } else {
      this.weaponSpr.setVisible(false);
    }
  }

  /* ------------------------------ 지상/공중 ------------------------------ */

  private updateGround(
    dt: number,
    input: InputSnapshot,
    climbables: readonly ClimbableInstance[],
  ): void {
    const body = this.body as Phaser.Physics.Arcade.Body;
    const onFloor = this.onFloor();

    if (this.wasAirborne && onFloor) this.events.emit(PLAYER_EVENT.land);
    this.wasAirborne = !onFloor;

    const dir = (input.right ? 1 : 0) - (input.left ? 1 : 0);
    const control = onFloor ? 1 : PLAYER.airControl;

    const stunned = this.playerState.hasStatus('stun');
    const speedMul = this.playerState.moveSpeedMul;

    if (stunned) {
      if (dir !== 0) {
        this.facing = dir > 0 ? 1 : -1;
        this.setFlipX(this.facing < 0);
      }
      if (onFloor) body.setVelocityX(0);
      this.nearClimbable = this.findClimbable(climbables);
      const nowOnFloor = this.onFloor();
      if (!nowOnFloor) this.setMode('air');
      else this.setMode('idle');
      return;
    }

    if (dir !== 0) {
      this.facing = dir > 0 ? 1 : -1;
      this.setFlipX(this.facing < 0);
      const target = PLAYER.moveSpeed * speedMul * dir;
      const next = body.velocity.x + PLAYER.accel * control * dt * dir;
      body.setVelocityX(dir > 0 ? Math.min(next, target) : Math.max(next, target));
    } else if (onFloor) {
      const decel = PLAYER.friction * dt;
      const vx = body.velocity.x;
      body.setVelocityX(Math.abs(vx) <= decel ? 0 : vx - Math.sign(vx) * decel);
    }

    // 사다리/밧줄 진입
    const climbable = this.findClimbable(climbables);
    this.nearClimbable = climbable;
    if (climbable) {
      const feet = this.y;
      // 짧게 탭한 경우(isDown 이 이미 false)도 잡히도록 단발 입력을 함께 본다
      const wantsUp = input.up || input.upPressed;
      const wantsDown = input.down || input.downPressed;
      if (wantsUp && feet > climbable.yTop + 4) {
        this.enterClimb(climbable);
        return;
      }
      if (wantsDown && onFloor && feet <= climbable.yTop + 8 && climbable.yBottom > feet + 20) {
        this.enterClimb(climbable);
        return;
      }
    }

    // 점프 / 발판 통과
    if (input.jumpPressed && onFloor) {
      if (input.down) {
        this.dropThroughTimer = PLAYER.dropThroughMs;
        body.setVelocityY(40);
      } else {
        body.setVelocityY(PLAYER.jumpVelocity);
        this.events.emit(PLAYER_EVENT.jump);
      }
    }

    const nowOnFloor = this.onFloor();
    if (!nowOnFloor) this.setMode('air');
    else this.setMode(Math.abs(body.velocity.x) > 12 ? 'walk' : 'idle');
  }

  /* -------------------------------- 등반 -------------------------------- */

  /**
   * 등반 대상 탐색. 점 하나가 아니라 캐릭터 몸통 사각형과의 겹침으로 판정해
   * 사다리 앞에 대충 서 있어도 잡히게 한다.
   */
  private findClimbable(climbables: readonly ClimbableInstance[]): ClimbableInstance | null {
    const bodyRect = new Phaser.Geom.Rectangle(
      this.x - BODY.width / 2,
      this.y - BODY.height,
      BODY.width,
      BODY.height,
    );
    let best: ClimbableInstance | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const c of climbables) {
      if (!Phaser.Geom.Intersects.RectangleToRectangle(c.rect, bodyRect)) continue;
      const distance = Math.abs(c.x - this.x);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = c;
      }
    }
    return best;
  }

  private enterClimb(target: ClimbableInstance): void {
    this.climbTarget = target;
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setAllowGravity(false);
    body.setVelocity(0, 0);
    this.x = target.x;
    this.y = Phaser.Math.Clamp(this.y, target.yTop + 8, target.yBottom);
    this.setFlipX(false);
    this.setMode('climb');
    this.events.emit(PLAYER_EVENT.climbEnter, target.kind);
  }

  private exitClimb(mode: PlayerMode = 'air'): void {
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setAllowGravity(true);
    this.climbTarget = null;
    this.setMode(mode);
  }

  private updateClimb(deltaMs: number, input: InputSnapshot): void {
    const target = this.climbTarget;
    const body = this.body as Phaser.Physics.Arcade.Body;
    if (!target) {
      this.exitClimb();
      return;
    }

    this.x = target.x;
    const dir = (input.down ? 1 : 0) - (input.up ? 1 : 0);
    body.setVelocity(0, dir * PLAYER.climbSpeed * this.playerState.moveSpeedMul);

    if (dir !== 0) {
      this.climbStepAccum += deltaMs;
      if (this.climbStepAccum > 220) {
        this.climbStepAccum = 0;
        this.events.emit(PLAYER_EVENT.climbStep);
      }
      this.anims.resume();
    } else {
      this.anims.pause();
    }

    // 맨 위로 올라서기 / 맨 아래에서 내려오기
    if (this.y <= target.yTop + 4 && (input.up || input.upPressed)) {
      this.y = target.yTop;
      body.setVelocity(0, 0);
      this.exitClimb('idle');
      return;
    }
    if (this.y >= target.yBottom && input.down) {
      this.y = target.yBottom;
      this.exitClimb('idle');
      return;
    }
    this.y = Phaser.Math.Clamp(this.y, target.yTop, target.yBottom);

    // 점프로 이탈
    if (input.jumpPressed) {
      const lateral = (input.right ? 1 : 0) - (input.left ? 1 : 0);
      this.exitClimb('air');
      body.setVelocityY(PLAYER.jumpVelocity * 0.82);
      if (lateral !== 0) {
        body.setVelocityX(PLAYER.ladderKickOff * lateral);
        this.facing = lateral > 0 ? 1 : -1;
        this.setFlipX(this.facing < 0);
      }
      this.events.emit(PLAYER_EVENT.jump);
    }
  }

  /* -------------------------------- 공격 -------------------------------- */

  tryAttack(skill: SkillDef): boolean {
    if (this.isBusy || this.mode === 'climb') return false;
    if (this.playerState.hasStatus('stun')) return false;
    if (this.cooldownLeft(skill.id) > 0) return false;
    if (skill.mpCost > 0 && !this.playerState.spendMp(skill.mpCost)) {
      this.events.emit(PLAYER_EVENT.noMp, skill);
      return false;
    }

    this.cooldowns.set(skill.id, skill.cooldownMs + skill.castMs);
    this.activeSkill = skill;
    this.attackElapsed = 0;
    this.attackHitFired = false;
    this.setMode('attack');

    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setVelocityX(skill.lungeVelocity * this.facing);

    const animKey =
      skill.anim === 'swing'
        ? ANIM.swing
        : skill.id === 'attack'
          ? ANIM.attack
          : ANIM.attackStrong;
    this.play(animKey, true);
    this.events.emit(PLAYER_EVENT.attackStart, skill);
    return true;
  }

  private updateAttack(deltaMs: number): void {
    const skill = this.activeSkill;
    if (!skill) {
      this.setMode('idle');
      return;
    }
    this.attackElapsed += deltaMs;

    if (!this.attackHitFired && this.attackElapsed >= skill.castMs * skill.hitFrameRatio) {
      this.attackHitFired = true;
      this.events.emit(PLAYER_EVENT.attackHit, skill);
    }

    const body = this.body as Phaser.Physics.Arcade.Body;
    if (this.onFloor()) body.setVelocityX(body.velocity.x * 0.86);

    if (this.attackElapsed >= skill.castMs) {
      this.activeSkill = null;
      this.setMode(this.onFloor() ? 'idle' : 'air');
    }
  }

  /**
   * 현재 공격 판정 사각형.
   *
   * 판정의 **아래쪽 변을 발밑 기준**으로 잡는다. 몸통 중심을 기준으로 잡으면
   * 달팽이처럼 키가 작은 몬스터(높이 28px)와 겹치는 구간이 몇 픽셀밖에 안 되어
   * 헛치는 일이 생긴다. docs/troubleshooting.md TS-0007 참고.
   */
  hitboxFor(skill: SkillDef): Phaser.Geom.Rectangle {
    const box = skill.hitbox;
    const cx = this.x + box.offsetX * this.facing;
    const bottom = this.y + 8 + box.offsetY;
    return new Phaser.Geom.Rectangle(
      cx - box.width / 2,
      bottom - box.height,
      box.width,
      box.height,
    );
  }

  /* ------------------------------- 피격/사망 ------------------------------- */

  takeHit(amount: number): boolean {
    if (this.isInvulnerable || this.mode === 'dead') return false;
    if (training.god) return false;

    const climbing = this.mode === 'climb';
    this.playerState.damage(amount);

    if (!this.playerState.alive) {
      if (climbing) this.exitClimb('air');
      this.die();
      return true;
    }

    this.invulnTimer = PLAYER.invulnMs;
    this.startBlink();
    this.events.emit(PLAYER_EVENT.hurt, amount);
    // 등반 중에는 넉백·hurt 전이를 하지 않아 사다리에서 떨어지지 않는다.
    if (!climbing) {
      this.hurtTimer = 320;
      this.setMode('hurt');
    }
    return true;
  }

  private die(): void {
    this.setMode('dead');
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setVelocity(0, -240);
    this.setTint(0x8a8a9a);
    this.setAngle(this.facing > 0 ? -70 : 70);
    this.events.emit(PLAYER_EVENT.died);
  }

  revive(x: number, y: number): void {
    this.blinkTween?.stop();
    this.blinkTween = null;
    this.clearTint();
    this.setAngle(0);
    this.setAlpha(1);
    this.setPosition(x, y);
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setAllowGravity(true);
    body.setVelocity(0, 0);
    this.invulnTimer = PLAYER.invulnMs;
    this.startBlink();
    this.setMode('idle');
  }

  private startBlink(): void {
    this.blinkTween?.stop();
    this.setAlpha(1);
    this.blinkTween = this.scene.tweens.add({
      targets: this,
      alpha: { from: 1, to: 0.25 },
      duration: 110,
      yoyo: true,
      repeat: Math.floor(PLAYER.invulnMs / 220),
      onComplete: () => {
        this.setAlpha(1);
        this.blinkTween = null;
      },
    });
  }

  /* ------------------------------ 상태/애니메이션 ------------------------------ */

  private onFloor(): boolean {
    const body = this.body as Phaser.Physics.Arcade.Body;
    return body.blocked.down || body.touching.down;
  }

  private setMode(next: PlayerMode): void {
    if (this.mode === next) return;
    this.mode = next;
    switch (next) {
      case 'idle':
        this.anims.resume();
        this.play(ANIM.stand, true);
        break;
      case 'walk':
        this.anims.resume();
        this.play(ANIM.walk, true);
        break;
      case 'air':
        this.anims.resume();
        this.play(ANIM.jump, true);
        break;
      case 'climb':
        this.anims.resume();
        this.play(this.climbTarget?.kind === 'rope' ? ANIM.rope : ANIM.ladder, true);
        break;
      case 'hurt':
      case 'dead':
        this.anims.resume();
        this.play(ANIM.hurt, true);
        break;
      case 'attack':
        break;
    }
  }

  /** 월드 경계 밖으로 떨어졌을 때 복구용 */
  clampToWorld(mapHeight: number): void {
    if (this.y > mapHeight + 200) {
      this.setPosition(this.x, 100);
      (this.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
    }
  }

  static get bodyHeight(): number {
    return BODY.height;
  }

  static get gravity(): number {
    return WORLD.gravity;
  }
}
